import React, {useCallback} from 'react';
import I18n from 'app/i18n';
import allSlotsIcon from 'images/marketplace/slot-all.png';
import armorIcon from 'images/marketplace/slot-armor.png';
import bootsIcon from 'images/marketplace/slot-boots.png';
import cloakIcon from 'images/marketplace/slot-cloak.png';
import helmetIcon from 'images/marketplace/slot-helmet.png';
import shieldIcon from 'images/marketplace/slot-shield.png';
import swordIcon from 'images/marketplace/slot-sword.png';

export const SLOT_PARTS = [
    'helmet',
    'armor',
    'sword',
    'shield',
    'cloak',
    'boots'
] as const;

export type MarketplaceSlotPart = typeof SLOT_PARTS[number];

const SLOT_IMAGES: Record<MarketplaceSlotPart, string> = {
    armor: armorIcon,
    boots: bootsIcon,
    cloak: cloakIcon,
    helmet: helmetIcon,
    shield: shieldIcon,
    sword: swordIcon
};

interface SlotFiltersProps {
    selected: MarketplaceSlotPart[];
    onChange: (parts: MarketplaceSlotPart[]) => void;
}

const togglePart = (selected: MarketplaceSlotPart[], part: MarketplaceSlotPart): MarketplaceSlotPart[] => selected.includes(part)
    ? selected.filter(item => item !== part)
    : selected.concat(part);

const SlotFilters = ({selected, onChange}: SlotFiltersProps): React.ReactElement => {
    const allSelected = SLOT_PARTS.every(part => selected.includes(part));
    const allSlotsLabel = I18n.t(`marketplace_slots.${allSelected ? 'clear_all' : 'select_all'}`);

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const part = event.currentTarget.dataset.slot;

        if (part) {
            onChange(togglePart(selected, part as MarketplaceSlotPart));
        }
    }, [onChange, selected]);

    const handleToggleAll = useCallback(() => {
        onChange(allSelected ? [] : [...SLOT_PARTS]);
    }, [allSelected, onChange]);

    return <div className="market-slot-filters" role="group" aria-label={I18n.t('marketplace_slots.label')}>
        {SLOT_PARTS.map((part: MarketplaceSlotPart) => {
            const active = selected.includes(part);

            return <button
                key={part}
                type="button"
                className={`market-slot-filter${active ? ' market-slot-filter--active' : ''}`}
                title={I18n.t(`marketplace_slots.${part}`)}
                data-slot={part}
                onClick={handleClick}
            >
                <img
                    className="market-slot-filter__icon"
                    src={SLOT_IMAGES[part]}
                    alt={I18n.t(`marketplace_slots.${part}`)}
                />
            </button>;
        })}
        <button
            type="button"
            className={`market-slot-filter market-slot-filter--all${allSelected ? ' market-slot-filter--active' : ''}`}
            title={allSlotsLabel}
            onClick={handleToggleAll}
        >
            <img
                className="market-slot-filter__icon market-slot-filter__icon--all"
                src={allSlotsIcon}
                alt={allSlotsLabel}
            />
        </button>
    </div>;
};

export default SlotFilters;
